import { ButtonCus } from '@/components/common/Button';
import Combobox from '@/components/common/Combobox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/common/Form';
import { DISPPLAY_MOMENT_YMD_HM, ekDomain, GROUP_CAPACITY_BIKE, MILEAGE_LIMIT_GROUP, ROUTES, STEP } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import MapWithSearch from '@/pages/booking/booking-bike/components/map/MapWithSearch';
import RuleKilometerLimit from '@/pages/booking/booking-bike/components/modal/RuleKilometerLimit';
import yup from '@/services/yup';
import { ADDITIONAL_CHARGE_TYPE, COMMON_TYPE, DROPOFF_OPTION, GROUP_CAPACITY_TYPE, INSURENCE_OPTION, OPTION_FORMAT, OptionFormatType, OTHER_SETTING_TYPE } from '@/types/enums';
import { formatDateTime, formatDateTimeISOJST } from '@/utils/datetime';
import { isPathEk } from '@/utils/routes';
import { formatCurrency } from '@/utils/formatNumber';
import { yupResolver } from '@hookform/resolvers/yup';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import BikeItem from '../BikeItem';
import { AdditionalService, ReservationServices } from '@/types/reservation';
import { GetOptionsEnduserResponse } from '@/types/http-payload/option';
import CustomDateTimePicker from '@/components/calendar/CustomReactDateTimePicker';
import { flowResult } from 'mobx';
import moment from 'moment';
import { Carousel, CarouselApi, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/common/Carousel';
import OptionComponent from '@/pages/booking/booking-bike/components/options/OptionComponent';
import OptionSelectItem from '@/pages/booking/booking-bike/components/options/OptionSelectItem';
import { OptionOtherSetting } from '@/types/option';
import { GetOtherSettingResponse } from '@/types/http-payload/setting';

interface InformationUpdateProps {
    next: Function,
    completeStep: number
}

export default observer(function InformationUpdate({ next, completeStep }: InformationUpdateProps) {
    // hook
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const { id } = useParams();
    const isEk = isPathEk(pathname);
    const navigate = useNavigate();
    // store
    const { reservationStore: { reservation, reservationForUpdate, setObservable, confirmDataForUpdateInfor },
        modalStore: { showModal }, locationStore: { locations, getLocations, paging: locationPaging },
        optionStore: { getOptionsEnduser, optionsForOrder }, generalStore: { otherSetting, getOtherSetting },
        locationBikeStore: { listBikeAvailable, getAllBikeAvailableInLocation }
    } = useStore();
    // validate schema
    const validateSchema = yup.object().shape({
        // date
        startTime: yup
            .string()
            .required('form_error_validate.required')
            .test(
                'startTime-before-endTime',
                t('form_error_validate.rental_date_time_before_return_date_time'),
                function (value) {
                    const { endTime } = this.parent;
                    if (!value || !endTime) return true;
                    const startMoment = moment(value);
                    const endMoment = moment(endTime);
                    if (!startMoment.isValid() || !endMoment.isValid()) return false;
                    return startMoment.isBefore(endMoment);
                }
            ),
        endTime: yup
            .string()
            .required('form_error_validate.required')
            .test(
                'endTime-after-startTime',
                t('form_error_validate.return_date_time_after_rental_date_time'),
                function (value) {
                    const { startTime } = this.parent;
                    if (!value || !startTime) return true;
                    const startMoment = moment(startTime);
                    const endMoment = moment(value);
                    if (!startMoment.isValid() || !endMoment.isValid()) return false;
                    return endMoment.isAfter(startMoment);
                }
            ),
        locationId: yup.string().required('form_error_validate.required'),
        bikeId: yup.string().required('form_error_validate.required'),
        additionalServices: yup.array().nullable()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    // state
    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
        }
    });

    const [unAvailableServices, setUnAvailableServices] = useState<ReservationServices>();
    const [selectedBike, setSelectedBike] = useState<any>();
    const [api, setApi] = useState<CarouselApi>();

    let watchStartTime = form.watch('startTime');
    let watchEndTime = form.watch('endTime');
    let watchSelectedLocation = form.watch('locationId');
    let watchAdditionalService = form.watch('additionalServices');
    let watchSelectedBike = form.watch('bikeId');

    // lifecycle
    useEffect(() => {
        getLocations({}, { ...locationPaging, pagination: { ...locationPaging.pagination, pageIndex: -2 } });
        getOptionsEnduser({
            type: isEk ? COMMON_TYPE.EK : COMMON_TYPE.GAS
        });
        getOtherSetting();
    }, []);

    useEffect(() => {
        if (id && reservation && Object.keys(reservation).length > 0 && optionsForOrder && Object.keys(optionsForOrder).length > 0 &&
            (!isEk ? Object.keys(otherSetting).length > 0 : true) && locations.length > 0) {
            let params: any = {};
            let bike: any;
            if (completeStep === STEP.STEP_2) {
                params = {
                    locationId: reservationForUpdate?.newOrder?.location?.id,
                    currentOrderId: id,
                    bikeId: reservationForUpdate?.newOrder?.bike?.id,
                    startTime: reservationForUpdate?.newOrder.dateAndTime.startTime && formatDateTimeISOJST(reservationForUpdate?.newOrder.dateAndTime.startTime),
                    endTime: reservationForUpdate?.newOrder.dateAndTime.endTime && formatDateTimeISOJST(reservationForUpdate?.newOrder.dateAndTime.endTime)
                };
                bike = reservationForUpdate?.newOrder?.bike;
            } else {
                params = {
                    locationId: reservation?.information?.location?.id,
                    currentOrderId: id,
                    bikeId: reservation?.information?.bike?.id,
                    startTime: reservation?.details.dateAndTime.rentalFrom && formatDateTimeISOJST(reservation?.details.dateAndTime.rentalFrom),
                    endTime: reservation?.details.dateAndTime.rentalTo && formatDateTimeISOJST(reservation?.details.dateAndTime.rentalTo)
                };
                bike = reservation?.information?.bike;
            }
            // form.setValue('');
            form.setValue('startTime', formatDateTime(params?.startTime || '', DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false }) || '');
            form.setValue('endTime', formatDateTime(params?.endTime || '', DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false }) || '');
            onLoadSetSelectedOption(reservation.details.services, optionsForOrder, otherSetting, completeStep, reservationForUpdate?.newOrder?.additionalServiceCost || null);
            if (locations.some(item => item.id.toString() === params.locationId.toString())) {
                form.setValue('locationId', params.locationId.toString());
                getAndSetBikeOfLocation(bike, params);
            }
        }
    }, [reservation, optionsForOrder, locations, otherSetting]);

    useEffect(() => {
        if (!api) return;
        const idx = listBikeAvailable.findIndex(
            b => b.id.toString() === watchSelectedBike
        );
        if (idx >= 0) {
            api.scrollTo(idx); // scroll ngang đến slide idx :contentReference[oaicite:0]{index=0}
        }
    }, [api, watchSelectedBike, listBikeAvailable]);

    // function
    const getAndSetBikeOfLocation = async (bike: any, params: any) => {
        const result = await flowResult(getAllBikeAvailableInLocation(params));
        if (result && result.length > 0) {
            if (result.some(item => item?.id.toString() === bike?.id.toString())) {
                form.setValue('bikeId', bike.id.toString());
                let currentBike = result.find(item => item?.id.toString() === bike?.id.toString());
                setSelectedBike(currentBike);
            } else {
                form.setValue('bikeId', '');
                setSelectedBike(undefined);
            }
        }
    };
    const [optionList, compensationList] = useMemo(() => {
        const { option = [], compensation = [], dropOff = [], extraMileage = [] } = optionsForOrder;

        return [option, compensation, dropOff, extraMileage];
    }, [optionsForOrder]);

    const [dropOffList] = useMemo(() => {
        let dropOffList: OptionOtherSetting[] = [];
        const { dropOff } = otherSetting;

        if (dropOff && dropOff.length > 0) {
            dropOffList = dropOff.map((item) => {
                return ({
                    name: `${formatCurrency(item.price)}${t('currency.currency_unit')}/1km\n貸出拠点起算`,
                    id: Number(item?.id || 0),
                    price: Number(item.price),
                    format: OPTION_FORMAT.DropOff,
                    status: 1
                });
            });
        }

        return [dropOffList];
    }, [otherSetting]);

    const [extraMileageList] = useMemo(() => {
        let extraMileageList: OptionOtherSetting[] = [];
        const { mileageLimit } = otherSetting;

        if (mileageLimit && Object.keys(mileageLimit).length > 0) {
            const { variable } = mileageLimit;
            if (selectedBike) {
                let bikeCapacityVal = selectedBike?.bikeCapacity?.id;
                extraMileageList = Object.keys(variable)
                    .filter(key => bikeCapacityVal ?
                        (Number(bikeCapacityVal) <= 2 ?
                            MILEAGE_LIMIT_GROUP[key].value === GROUP_CAPACITY_TYPE.LQ_125CC :
                            MILEAGE_LIMIT_GROUP[key].value === GROUP_CAPACITY_TYPE.GQ_126CC) :
                        key)
                    .map((key) => {
                        return ({
                            id: variable[key][0].id,
                            // name: `${t('price_list.title_section.mileage_limit')}:${MILEAGE_LIMIT_GROUP[key].label}`,
                            name: MILEAGE_LIMIT_GROUP[key].label,
                            label: `${t('price_list.title_section.mileage_limit')}: ${MILEAGE_LIMIT_GROUP[key].label}`,
                            price: variable[key][0].price,
                            maxValue: 10,
                            status: 1,
                            format: OPTION_FORMAT.ExtraMileage,
                            mileageLimit: `/${MILEAGE_LIMIT_GROUP[key].km}km`,
                            capacityType: MILEAGE_LIMIT_GROUP[key].value,
                            formatSetting: OTHER_SETTING_TYPE.MILEAGE_LIMIT,
                            additionalChargesType: ADDITIONAL_CHARGE_TYPE.VARIABLE
                        });
                    });
            }
        }

        return [extraMileageList];
    }, [otherSetting, selectedBike]);

    const handleChangeQuantity = (value: string | number, id: number | string, price: number | string | null = 0, format: OptionFormatType) => {
        if (watchAdditionalService && watchAdditionalService.length > 0) {
            const findOption = watchAdditionalService?.findIndex(item => item.id === Number(id) && item.format === format);
            if (findOption > -1) {
                let cloneAdditionalSrv = [...watchAdditionalService];
                if (Number(value) > 0) {
                    cloneAdditionalSrv[findOption].value = Number(value);
                } else {
                    cloneAdditionalSrv = cloneAdditionalSrv.filter((_, indx) => indx !== findOption);
                }
                form.setValue('additionalServices', [...cloneAdditionalSrv]);
            } else {
                Number(value) > 0 && form.setValue('additionalServices', [...watchAdditionalService, { id: id, value: Number(value), price: price, format }]);
            }
        } else {
            Number(value) > 0 && form.setValue('additionalServices', [{ id: id, value: Number(value), price: price, format }]);
        }
    };

    const handleChangeQuantityExtraMileage = (value: string | number, selectedItem: OptionOtherSetting) => {
        const { id, price, label: name, formatSetting, capacityType, additionalChargesType } = selectedItem;
        let service = {
            id: id, value: Number(value), price: price, format: OPTION_FORMAT.ExtraMileage, name, formatSetting, capacityType, additionalChargesType
        };
        if (watchAdditionalService && watchAdditionalService.length > 0) {
            const findOption = watchAdditionalService?.findIndex(item => String(item.id) === String(id) && Number(item.capacityType) === Number(service.capacityType));
            if (findOption > -1) {
                let cloneAdditionalSrv = [...watchAdditionalService];
                if (Number(value) > 0) {
                    cloneAdditionalSrv[findOption].value = Number(value);
                } else {
                    cloneAdditionalSrv = cloneAdditionalSrv.filter((_, indx) => indx !== findOption);
                }
                form.setValue('additionalServices', [...cloneAdditionalSrv]);
            } else {
                Number(value) > 0 && form.setValue('additionalServices', [...watchAdditionalService, { ...service }]);
            }
        } else {
            Number(value) > 0 && form.setValue('additionalServices', [{ ...service }]);
        }
    };

    const onLoadSetSelectedOption = (services: ReservationServices, options: GetOptionsEnduserResponse, mileageDropOffSetting: GetOtherSettingResponse, step: number, newService?: AdditionalService) => {
        const { option: optionA = [], compensation: compensationA = [], dropOff: dropOffA = [], extraMileage = [] } = services;
        const { option: optionB = [], compensation: compensationB = [] } = options;
        let notAvailableOptions = {
            option: filterById(optionA, optionB),
            compensation: filterById(compensationA.filter(item => Number(item.id) > 0), compensationB)
        } as ReservationServices;

        let selectedOptions: any = [];
        if (completeStep === STEP.STEP_1) {
            selectedOptions = Object.keys(services).filter((key) => {
                return Object.keys(notAvailableOptions).includes(key);
            }).reduce((prev, cur) => {
                if (services[cur] && services[cur].length > 0) {
                    prev = [...prev, ...services[cur].filter(item => Number(item.id) > 0 && !notAvailableOptions[cur].some(o => Number(o.id) === Number(item.id)))
                        .map(item => ({
                            id: Number(item.id),
                            value: item.quantity,
                            format: item?.format
                        }))] as any;
                }

                return prev;
            }, []);

            const { mileageLimit, dropOff } = mileageDropOffSetting;
            if (dropOffA && dropOffA.length > 0) {
                const dropOffItem = dropOff[0];
                selectedOptions = [
                    ...selectedOptions,
                    {
                        id: dropOffItem.id,
                        format: OPTION_FORMAT.DropOff,
                        name: t('price_list.title_section.drop_off'),
                        capacityType: GROUP_CAPACITY_TYPE.NONE,
                        additionalChargesType: ADDITIONAL_CHARGE_TYPE.NONE,
                        formatSetting: OTHER_SETTING_TYPE.DROP_OFF
                    }
                ];
            }
            if (extraMileage.length > 0) {
                const { variable } = mileageLimit;
                extraMileage.forEach((item) => {
                    if (item?.mileageChargeType === GROUP_CAPACITY_TYPE.LQ_125CC) {
                        selectedOptions = [
                            ...selectedOptions,
                            {
                                id: variable?.capacity125cc[0]?.id,
                                name: item.name,
                                format: OPTION_FORMAT.ExtraMileage,
                                price: variable?.capacity125cc[0]?.price,
                                value: item?.quantity,
                                capacityType: GROUP_CAPACITY_TYPE.LQ_125CC,
                                additionalChargesType: ADDITIONAL_CHARGE_TYPE.VARIABLE,
                                formatSetting: OTHER_SETTING_TYPE.MILEAGE_LIMIT
                            }
                        ];
                    } else if (item?.mileageChargeType === GROUP_CAPACITY_TYPE.GQ_126CC) {
                        selectedOptions = [
                            ...selectedOptions,
                            {
                                id: variable?.capacity126cc[0]?.id,
                                name: item.name,
                                format: OPTION_FORMAT.ExtraMileage,
                                price: variable?.capacity126cc[0]?.price,
                                value: item?.quantity,
                                capacityType: GROUP_CAPACITY_TYPE.GQ_126CC,
                                additionalChargesType: ADDITIONAL_CHARGE_TYPE.VARIABLE,
                                formatSetting: OTHER_SETTING_TYPE.MILEAGE_LIMIT
                            }
                        ];
                    }
                });
            }
        } else {
            if (newService && Object.keys(newService).length > 0) {
                const { optionSelect = [], compensation = [], dropOff = [], extraMileage = [] } = newService || {};
                let cvServices = [
                    ...optionSelect,
                    ...compensation,
                    ...extraMileage,
                    ...dropOff
                ];
                // console.log(cvServices);
                selectedOptions = cvServices.map(item => ({
                    ...item,
                    id: Number(item.id),
                    value: item.value,
                    format: item.format
                }));
            }
        }
        form.setValue('additionalServices', [...selectedOptions]);
        setUnAvailableServices(notAvailableOptions);
    };

    const filterById = (arrA: any[], arrB: any[]) => arrA.filter(a => !arrB.some(b => Number(b.id) === Number(a.id)));

    const showKilometerLimit = () => {
        showModal({
            id: 'kilometer-limit',
            title: '',
            size: '7xl',
            titleClassName: 'text-center max-w-none',
            content: (
                <RuleKilometerLimit />
            ),
            footerClassName: 'hidden'
        });
    };

    const handleSelectBike = (bike: any) => {
        if (bike && bike.id) {
            form.setValue('bikeId', bike.id);
            // Check if capacity of new selected bike changes then not in group capacity --> reset option extra mileage
            if (selectedBike) {
                let currentCapacity = selectedBike?.bikeCapacity?.id;
                let newCapacity = bike?.bikeCapacity?.id;
                if (newCapacity && currentCapacity) {
                    if (newCapacity !== currentCapacity) {
                        let arr = [newCapacity, currentCapacity];
                        if (!(arr.every(item => GROUP_CAPACITY_BIKE[GROUP_CAPACITY_TYPE.LQ_125CC].includes(item)) ||
                            arr.every(item => GROUP_CAPACITY_BIKE[GROUP_CAPACITY_TYPE.GQ_126CC].includes(item)))) {
                            if (watchAdditionalService && watchAdditionalService.length > 0) {
                                let cloneAdditionalSrv = [...watchAdditionalService];
                                let filterSrv = cloneAdditionalSrv.filter(srv => srv?.format !== OPTION_FORMAT.ExtraMileage);
                                form.setValue('additionalServices', [...filterSrv]);
                            }
                        }
                    }
                }
            }
            setSelectedBike(bike);
        }
    };

    const handleChangeTime = (dateTime: string, isStartTime: boolean) => {
        if (watchSelectedLocation) {
            let params = {
                locationId: watchSelectedLocation,
                currentOrderId: id,
                bikeId: form.getValues('bikeId'),
                startTime: formatDateTimeISOJST(isStartTime ? dateTime : watchStartTime, false),
                endTime: formatDateTimeISOJST(isStartTime ? watchEndTime : dateTime, false)
            };
            // form.setValue('bikeId', '');
            getAndSetBikeOfLocation(selectedBike, params);
            // setSelectedBike(undefined);
        }
    };

    const handleChangeLocation = (locationId?: string | number) => {
        if (locationId) {
            let params = {
                locationId: locationId,
                currentOrderId: id,
                startTime: formatDateTimeISOJST(watchStartTime, false),
                endTime: formatDateTimeISOJST(watchEndTime, false)
            };
            form.setValue('bikeId', '');
            getAndSetBikeOfLocation(selectedBike, params);
        }
    };

    const onSubmit = async (data: FormData) => {
        let payload = {
            startTime: data?.startTime && formatDateTimeISOJST(data?.startTime, false),
            endTime: data?.endTime && formatDateTimeISOJST(data?.endTime, false),
            locationId: data.locationId,
            bikeId: data.bikeId,
            additionalServices: data.additionalServices,
            id: Number(id)
        };

        const result = await flowResult(confirmDataForUpdateInfor(payload));
        if (result) {
            setObservable('reservationForUpdate', result, { isMergeObject: true });
            next && next();
        }
    };

    const onCancel = () => {
        if (isEk) {
            navigate(`/${ekDomain}${ROUTES.reservationManagement.href}/${ROUTES.reservationDetail.href}/${id}`);
        } else {
            navigate(`${ROUTES.reservationManagement.href}/${ROUTES.reservationDetail.href}/${id}`);
        }
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
                <div className='flex flex-col gap-3'>
                    <div className='flex flex-col gap-3 layout-card'>
                        <div>
                            <FormLabel required>{t('field.date&time')}</FormLabel>
                            <div className='grid grid-cols-2 gap-4'>
                                <FormField
                                    name='startTime'
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormControl>
                                                <CustomDateTimePicker
                                                    value={field.value}
                                                    onChange={(date) => {
                                                        const formatted = date ? (moment.isMoment(date) ? date.format(DISPPLAY_MOMENT_YMD_HM) : date) : null;
                                                        field.onChange(formatted);
                                                        handleChangeTime(formatted, true);
                                                        const endVal = form.getValues('endTime');
                                                        const endMoment = endVal ? moment(endVal) : null;
                                                        const startMoment = formatted ? moment(formatted) : null;
                                                        if (
                                                            startMoment?.isValid() &&
                                                            endMoment?.isValid() &&
                                                            startMoment.isBefore(endMoment)
                                                        ) {
                                                            form.clearErrors('endTime');
                                                        }
                                                    }}
                                                    inputProps={
                                                        {
                                                            placeholder: t('field.rental_date_and_time'),
                                                            readOnly: true
                                                        }
                                                    }

                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    name='endTime'
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormControl>
                                                <CustomDateTimePicker
                                                    value={field.value}
                                                    onChange={(date) => {
                                                        const formatted = date ? (moment.isMoment(date) ? date.format(DISPPLAY_MOMENT_YMD_HM) : date) : null;
                                                        field.onChange(formatted);
                                                        handleChangeTime(formatted, false);
                                                        const startVal = form.getValues('startTime');
                                                        const startMoment = startVal ? moment(startVal) : null;
                                                        const endMoment = formatted ? moment(formatted) : null;
                                                        if (
                                                            startMoment?.isValid() &&
                                                            endMoment?.isValid() &&
                                                            startMoment.isBefore(endMoment)
                                                        ) {
                                                            form.clearErrors('startTime');
                                                        }
                                                    }}
                                                    inputProps={
                                                        {
                                                            placeholder: t('field.return_date_and_time'),
                                                            readOnly: true
                                                        }
                                                    }
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>
                        <div>
                        </div>
                        <div className='grid grid-cols-2 gap-4'>
                            <FormField
                                control={form.control}
                                name='locationId'
                                render={({ field }) => (
                                    <FormItem className='w-full'>
                                        <FormLabel required>{t('field.location_name')}</FormLabel>
                                        <FormControl>
                                            <Combobox
                                                placeholder={t('field.location_name')}
                                                value={field.value?.toString() ?? ''}
                                                onChange={(item) => {
                                                    field.onChange(item?.value ?? '');
                                                    handleChangeLocation(item?.value ?? '');
                                                }}
                                                disableClearIcon
                                                items={
                                                    (Array.isArray(locations) && locations?.length ?
                                                        [
                                                            ...locations.map(item => ({
                                                                label: item.name ?? '',
                                                                value: item.id.toString(),
                                                                code: item.id
                                                            }))
                                                        ] :
                                                        []
                                                    )
                                                }
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div>
                            <FormField
                                control={form.control}
                                name='bikeId'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required>{t('field.list_bike_location')}</FormLabel>
                                        <FormControl>
                                            <div className='relative'>
                                                {
                                                    listBikeAvailable && listBikeAvailable.length > 0 ?
                                                        (
                                                            <Carousel
                                                                setApi={setApi}
                                                                opts={{
                                                                    align: 'center', // căn slide giữa
                                                                    duration: 50 // tăng từ 25 lên 50 để scroll chậm hơn
                                                                }}
                                                                className='mx-4'
                                                            >
                                                                <CarouselPrevious className='border-none group hover:bg-customRed opacity-0 xl:opacity-100 absolute top-1/2 lg:-left-[6px] transform -translate-y-1/2 -translate-x-full shadow-shadow-3 z-50' />
                                                                <CarouselContent className='w-full snap-x snap-mandatory'>
                                                                    {listBikeAvailable.map((item, slideIndex) => (
                                                                        <CarouselItem
                                                                            key={slideIndex}
                                                                            className='basis-[80%] point-600:basis-[50%] md:basis-[40%] point-940:basis-[30%] xl:basis-[21.5%]'
                                                                        >
                                                                            <BikeItem
                                                                                key={item.id}
                                                                                item={item}
                                                                                active={field.value === item.id.toString()}
                                                                                onCallBack={handleSelectBike}
                                                                            />
                                                                        </CarouselItem>
                                                                    ))}
                                                                </CarouselContent>
                                                                <CarouselNext className='border-none group hover:bg-customRed opacity-0 xl:opacity-100 absolute top-1/2 lg:-right-[5px] transform -translate-y-1/2 translate-x-full shadow-shadow-3 z-50' />
                                                            </Carousel>
                                                        ) :
                                                        <p className='w-full text-center'>{t('sentences.no_data')}</p>
                                                }
                                            </div>

                                        </FormControl>

                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>
                    <div className=''>
                        <h3 className='text-[1.0625rem]'>
                            <Trans
                                i18nKey='field.select_an_option_for_bike'
                                values={{ bike_model: selectedBike?.bikeModel || '' }}
                                components={{
                                    1: <span className='text-[#F2A80B] ml-1'></span>,
                                    2: <span></span>
                                }}
                            />
                            {/* <span>
                                {t('field.select_an_option_for_bike')}
                            </span>
                            {' '}
                            {selectedBike && <span className='text-[#F2A80B] ml-1'>{selectedBike.bikeModel}</span>} */}
                        </h3>
                        <div className='flex flex-col layout-card gap-3 mt-2'>
                            {/* Option */}
                            <div className=''>
                                <FormField
                                    control={form.control}
                                    name='additionalServices'
                                    render={() => (
                                        <FormItem className=''>
                                            <OptionComponent
                                                option={{
                                                    type: OPTION_FORMAT.Option,
                                                    title: 'オプション',
                                                    textDecoration: '用品',
                                                    isPrice: false,
                                                    inputChildren: (
                                                        <>
                                                            {optionList?.length > 0 && optionList.map((item, index) => {
                                                                return (
                                                                    <FormField
                                                                        key={index}
                                                                        control={form.control}
                                                                        name='additionalServices'
                                                                        render={({ field }) => (
                                                                            <FormItem className='flex gap-2'>
                                                                                <FormControl>
                                                                                    <OptionSelectItem
                                                                                        option={item}
                                                                                        type={OPTION_FORMAT.Option}
                                                                                        onChangeValueFn={(value) => {
                                                                                            handleChangeQuantity(value, item.id, item.price, OPTION_FORMAT.Option);
                                                                                        }}
                                                                                        selectedValue={watchAdditionalService?.find(srv => (srv.id === item.id && Number(srv.format) === OPTION_FORMAT.Option))?.value || 0}
                                                                                    />
                                                                                </FormControl>

                                                                            </FormItem>
                                                                        )}
                                                                    />
                                                                );
                                                            })}
                                                            {unAvailableServices?.option && unAvailableServices?.option.length > 0 && unAvailableServices.option.map((item, index) => {
                                                                let name = (
                                                                    <>
                                                                        <span className='opacity-75'>{item.name}</span>
                                                                        <span className='text-customRed italic'>{` (${t('words_title.unavailable')})`}</span>
                                                                    </>
                                                                );
                                                                let cvItem = {
                                                                    ...item,
                                                                    maxValue: item?.quantity,
                                                                    name
                                                                } as OptionOtherSetting;

                                                                return (
                                                                    <OptionSelectItem
                                                                        key={index}
                                                                        option={cvItem}
                                                                        disabled
                                                                        type={OPTION_FORMAT.Option}
                                                                        onChangeValueFn={(value) => {
                                                                            handleChangeQuantity(value, item.id, item.price, OPTION_FORMAT.Option);
                                                                        }}
                                                                        selectedValue={item?.quantity?.toString() || '0'}
                                                                    />
                                                                );
                                                            })}
                                                        </>)
                                                }}
                                            />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            {
                                !isEk && (
                                    <>
                                        {/* Compensation */}
                                        <FormField
                                            control={form.control}
                                            name='additionalServices'
                                            render={({ field }) => {
                                                // Tìm phần tử đã chọn thuộc compensationList (nếu có)
                                                const currentCompensation = field.value?.find(service =>
                                                    compensationList?.some(item => (item.id === service.id && service?.format === OPTION_FORMAT.Insurance)));

                                                return (
                                                    <FormItem className=''>
                                                        <OptionComponent
                                                            option={{
                                                                type: OPTION_FORMAT.Insurance,
                                                                title: '車両補償オプション料金',
                                                                textDecoration: '車両補償オプション',
                                                                isPrice: false,
                                                                // descriptionList: [
                                                                //     '立ちゴケ安心制度の対応範囲を超えてバイクを破損させてしまった場合のための補償です。',
                                                                //     '車両補償オプションを選んでおくことで、決められた金額の補償を受けることが出来ます。',
                                                                //     'レッカーなどの付帯サービスについて詳しくはこちらをご覧下さい。'
                                                                // ],
                                                                // descriptionCompList: [
                                                                //     <Link
                                                                //         target='_blank'
                                                                //         className='cursor-pointer hoverable:hover:underline text-primaryWeb'
                                                                //         key={compensationList?.length}
                                                                //         to={`${isEk ? ekDomain : ''}/${ROUTES.priceList.href}#towing-service`}
                                                                //     >
                                                                //         &gt;&gt;詳しくはこちら
                                                                //     </Link>
                                                                // ],
                                                                // specialNote: [
                                                                //     '※通常の車両補償は免責5万円、最大30万円まで補償',
                                                                //     '※Premiumクラスは免責5万円、最大80万円まで補償',
                                                                //     '※ 最大30日までの休業補償が無料で付帯いたします'
                                                                // ],
                                                                inputChildren: (
                                                                    <>
                                                                        {compensationList?.length > 0 && compensationList.map((item, index) => {
                                                                            const currentOptionValue = (currentCompensation?.id === item.id && item.format === OPTION_FORMAT.Insurance) ? INSURENCE_OPTION.SUBSCRIBE : INSURENCE_OPTION.NOT_SUBSCRIBE;

                                                                            return (
                                                                                <FormField
                                                                                    key={index}
                                                                                    control={form.control}
                                                                                    name='additionalServices'
                                                                                    render={({ field }) => (
                                                                                        <FormItem className='flex gap-2'>
                                                                                            <FormControl>
                                                                                                <OptionSelectItem
                                                                                                    option={item}
                                                                                                    type={OPTION_FORMAT.Insurance}
                                                                                                    isCheckOffOption
                                                                                                    onChangeValueFn={(value) => {
                                                                                                        if (value === INSURENCE_OPTION.SUBSCRIBE.toString()) {
                                                                                                            // Lọc bỏ tất cả các object thuộc compensationList trong additionalServices
                                                                                                            const otherServices = field.value?.filter(service => service?.format !== OPTION_FORMAT.Insurance) || [];
                                                                                                            // Thêm object được chọn vào additionalServices
                                                                                                            field.onChange([
                                                                                                                ...otherServices,
                                                                                                                { id: item.id, value: null, price: item.price, format: OPTION_FORMAT.Insurance }
                                                                                                            ]);
                                                                                                        } else {
                                                                                                            if (currentCompensation?.id.toString() === item.id.toString()) {
                                                                                                                field.onChange(field.value?.filter(service => service?.format !== OPTION_FORMAT.Insurance) || []);
                                                                                                            }
                                                                                                        }
                                                                                                    }}
                                                                                                    selectedValue={String(currentOptionValue)}
                                                                                                />
                                                                                            </FormControl>

                                                                                        </FormItem>
                                                                                    )}
                                                                                />
                                                                            );
                                                                        })}
                                                                        {unAvailableServices?.compensation && unAvailableServices?.compensation.length > 0 && unAvailableServices.compensation.map((item, index) => {
                                                                            const currentOptionValue = currentCompensation?.id === item.id && item.format === OPTION_FORMAT.Insurance ? INSURENCE_OPTION.SUBSCRIBE : INSURENCE_OPTION.NOT_SUBSCRIBE;
                                                                            let name = (
                                                                                <>
                                                                                    <span className='opacity-75'>{item.name}</span>
                                                                                    <span className='text-customRed italic'>{` (${t('words_title.unavailable')})`}</span>
                                                                                </>
                                                                            );

                                                                            return (
                                                                                <OptionSelectItem
                                                                                    key={index}
                                                                                    option={{ ...item, name } as OptionOtherSetting}
                                                                                    type={OPTION_FORMAT.Insurance}
                                                                                    isCheckOffOption
                                                                                    disabled
                                                                                    onChangeValueFn={(value) => {
                                                                                        if (value === INSURENCE_OPTION.SUBSCRIBE.toString()) {
                                                                                            // Lọc bỏ tất cả các object thuộc compensationList trong additionalServices
                                                                                            const otherServices = field.value?.filter(service => service?.format !== OPTION_FORMAT.Insurance) || [];
                                                                                            // Thêm object được chọn vào additionalServices
                                                                                            field.onChange([
                                                                                                ...otherServices,
                                                                                                { id: item.id, value: null, price: item.price, format: OPTION_FORMAT.Insurance }
                                                                                            ]);
                                                                                        } else {
                                                                                            if (currentCompensation?.id.toString() === item.id.toString()) {
                                                                                                field.onChange(field.value?.filter(service => service?.format !== OPTION_FORMAT.Insurance) || []);
                                                                                            }
                                                                                        }
                                                                                    }}
                                                                                    selectedValue={String(currentOptionValue)}
                                                                                />
                                                                            );
                                                                        })}
                                                                    </>)
                                                            }}
                                                        />
                                                    </FormItem>
                                                );
                                            }}
                                        />

                                        {/* Extra Mileage */}
                                        <FormField
                                            control={form.control}
                                            name='additionalServices'
                                            render={() => (
                                                <FormItem className=''>
                                                    <OptionComponent
                                                        option={{
                                                            type: OPTION_FORMAT.ExtraMileage,
                                                            title: '長距離走行オプション料金',
                                                            textDecoration: '長距離走行オプション',
                                                            // descriptionList: [
                                                            //     '基準となる走行距離を超えて走行をする場合、下記のオプションを追加して下さい。',
                                                            //     '各レンタル期間ごとの基準となる走行距離と、オプションの計算方法について詳しくはこちらをご覧下さい。'
                                                            // ],
                                                            // descriptionCompList: [
                                                            //     <div key='link' onClick={showKilometerLimit} className='w-fit cursor-pointer text-primaryWeb hoverable:hover:underline'>&gt;&gt;詳しくはこちら</div>
                                                            // ],
                                                            otherComponent: (
                                                                <MapWithSearch className='mb-5' showDescription={showKilometerLimit} />
                                                            ),
                                                            isPrice: false,
                                                            inputChildren: (
                                                                <>
                                                                    {extraMileageList?.length > 0 && extraMileageList.map((item, index) => {
                                                                        return (
                                                                            <FormField
                                                                                key={index}
                                                                                control={form.control}
                                                                                name='additionalServices'
                                                                                render={({ field }) => (
                                                                                    <FormItem className='flex gap-2'>
                                                                                        <FormControl>
                                                                                            <OptionSelectItem
                                                                                                option={item}
                                                                                                useSearch={false}
                                                                                                type={OPTION_FORMAT.ExtraMileage}
                                                                                                onChangeValueFn={(value) => {
                                                                                                    handleChangeQuantityExtraMileage(value, item);
                                                                                                }}
                                                                                                selectedValue={watchAdditionalService?.find(srv => (String(srv.id) === String(item.id) && Number(srv.format) === OPTION_FORMAT.ExtraMileage))?.value || 0}
                                                                                            />
                                                                                        </FormControl>

                                                                                    </FormItem>
                                                                                )}
                                                                            />
                                                                        );
                                                                    })}
                                                                </>)
                                                        }}
                                                    />
                                                </FormItem>
                                            )}
                                        />

                                        {/* Drop-off */}
                                        <FormField
                                            control={form.control}
                                            name='additionalServices'
                                            render={() => (
                                                <FormItem className=''>
                                                    <OptionComponent
                                                        option={{
                                                            type: OPTION_FORMAT.DropOff,
                                                            title: '乗り捨てオプション料金',
                                                            textDecoration: '乗り捨てオプション',
                                                            // descriptionList: ['乗り捨てオプションにつきましては、ご予約完了の後に別途メールにて乗り捨て拠点を確認させて頂き、正確な金額を算出させて頂きます。'],
                                                            isPrice: false,
                                                            inputChildren: (
                                                                <>
                                                                    {dropOffList?.length > 0 && dropOffList.map((item, index) => {
                                                                        const currentOptionValue = watchAdditionalService?.find(option => (String(option?.id) === String(item?.id) && Number(option.format) === OPTION_FORMAT.DropOff)) ? DROPOFF_OPTION.ADD : DROPOFF_OPTION.NOT_ADD;

                                                                        return (
                                                                            <FormField
                                                                                key={index}
                                                                                control={form.control}
                                                                                name='additionalServices'
                                                                                render={({ field }) => (
                                                                                    <FormItem className='flex gap-2'>
                                                                                        <FormControl>
                                                                                            <OptionSelectItem
                                                                                                option={item}
                                                                                                optionNameClass='font-normal'
                                                                                                type={OPTION_FORMAT.DropOff}
                                                                                                isCheckOffOption
                                                                                                onChangeValueFn={(value) => {
                                                                                                    if (value === DROPOFF_OPTION.ADD.toString()) {
                                                                                                        field.onChange([
                                                                                                            ...field.value || [],
                                                                                                            {
                                                                                                                id: item.id, value: null,
                                                                                                                price: item.price,
                                                                                                                format: OPTION_FORMAT.DropOff,
                                                                                                                name: t('price_list.title_section.drop_off'),
                                                                                                                formatSetting: OTHER_SETTING_TYPE.DROP_OFF,
                                                                                                                capacityType: GROUP_CAPACITY_TYPE.NONE,
                                                                                                                additionalChargesType: ADDITIONAL_CHARGE_TYPE.NONE
                                                                                                            }
                                                                                                        ]);
                                                                                                    } else {
                                                                                                        field.onChange(field.value?.filter(service => Number(service?.format) !== OPTION_FORMAT.DropOff) || []);
                                                                                                    }
                                                                                                }}
                                                                                                selectedValue={currentOptionValue.toString()}
                                                                                            />
                                                                                        </FormControl>

                                                                                    </FormItem>
                                                                                )}
                                                                            />
                                                                        );
                                                                    })}
                                                                </>)
                                                        }}
                                                    />
                                                </FormItem>
                                            )}
                                        />
                                    </>
                                )
                            }

                        </div>
                    </div>
                    <div className='flex justify-center text-[0.875rem] gap-2 md:gap-4'>
                        <ButtonCus type='button' onClick={onCancel} variant='outline' size='sm'>{t('button.cancel')}</ButtonCus>
                        <ButtonCus type='submit' disabled={form.formState.isSubmitting} variant='default' size='sm'>{t('button.next')}</ButtonCus>
                    </div>
                </div>
            </form>
        </Form>
    );
});
